import clsx from "clsx";
import type { ReactNode } from "react";

export function Spinner({ className }: { className?: string }) {
    return (
        <span
            className={clsx(
                "inline-block animate-spin rounded-full border-2 border-current border-t-transparent",
                className ?? "h-5 w-5",
            )}
        />
    );
}

export function LoadingBlock({ label = "Loading..." }: { label?: string }) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
            <Spinner className="h-7 w-7 text-[var(--color-primary)]" />
            <p className="app-shell-text-muted text-sm">{label}</p>
        </div>
    );
}

interface EmptyStateProps {
    title: string;
    description?: string;
    icon?: ReactNode;
    action?: ReactNode;
    className?: string;
}

export function EmptyState({ title, description, icon, action, className }: EmptyStateProps) {
    return (
        <div className={clsx("flex flex-col items-center justify-center gap-3 px-6 py-14", className)}>
            {icon && <div className="app-content-subtle rounded-2xl p-3">{icon}</div>}
            <div className="text-center">
                <p className="app-text-primary font-semibold">{title}</p>
                {description && (
                    <p className="app-shell-text-muted mt-1 max-w-sm text-sm">{description}</p>
                )}
            </div>
            {action}
        </div>
    );
}

export function ErrorState({
    message,
    onRetry,
}: {
    message: string;
    onRetry?: () => void;
}) {
    return (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-14">
            <p className="text-sm font-medium text-[var(--color-error-text)]">{message}</p>
            {onRetry && (
                <button
                    type="button"
                    onClick={onRetry}
                    className="app-text-primary text-sm font-semibold underline"
                >
                    Try again
                </button>
            )}
        </div>
    );
}

export function Skeleton({ className }: { className?: string }) {
    return <div className={clsx("app-content-subtle animate-pulse rounded-xl", className)} />;
}
